import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { apiRequest } from '../services/apiClient';
import { useFetch } from '../hooks/useFetch';

// Exported so leaf components can read auth via useContext directly
// (returning null outside a provider) instead of useAuth which throws.
// Used by W5 Workforce Hub tabs to permission-gate visibility without
// breaking tests that render the page outside the AuthProvider.
export const AuthContext = createContext(null);

function looksLikeUnauthorized(message) {
  if (!message) {
    return false;
  }
  return /\b401\b|unauthorized|not (?:logged in|authenticated)/i.test(message);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [authError, setAuthError] = useState('');

  const {
    data: fetchedUser,
    isLoading,
    error: fetchError
  } = useFetch('/api/auth/me');

  useEffect(() => {
    if (isLoading) {
      return;
    }

    if (fetchError) {
      // 401 means "not logged in" — surface as anonymous, not as an error.
      if (looksLikeUnauthorized(fetchError)) {
        setUser(null);
        setAuthError('');
      } else {
        setAuthError(fetchError);
      }
      return;
    }

    setAuthError('');
    setUser(fetchedUser);
  }, [fetchedUser, fetchError, isLoading]);

  const login = useCallback(async ({ identifier, email, password, workspaceSlug }) => {
    const result = await apiRequest('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ identifier: identifier || email, password, workspaceSlug })
    });

    setUser(result.user);
    setAuthError('');
    return result.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiRequest('/api/auth/logout', {
        method: 'POST'
      });
    } finally {
      setUser(null);
    }
  }, []);

  const changePassword = useCallback(async ({ currentPassword, newPassword }) => {
    const result = await apiRequest('/api/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword })
    });

    setUser(result.user);
    return result.user;
  }, []);

  const refreshCurrentUser = useCallback(async () => {
    const currentUser = await apiRequest('/api/auth/me');
    setUser(currentUser);
    setAuthError('');
    return currentUser;
  }, []);

  const enterClientPortal = useCallback(async ({ companySlug }) => {
    await apiRequest('/api/platform/client-context', {
      method: 'POST',
      body: JSON.stringify({ companySlug })
    });

    return refreshCurrentUser();
  }, [refreshCurrentUser]);

  const exitClientPortal = useCallback(async () => {
    await apiRequest('/api/platform/client-context', {
      method: 'DELETE'
    });

    return refreshCurrentUser();
  }, [refreshCurrentUser]);

  const resetUserPassword = useCallback(async ({ userId, newPassword }) => {
    return apiRequest(`/api/users/${userId}/reset-password`, {
      method: 'POST',
      body: JSON.stringify({ newPassword })
    });
  }, []);

  const hasPermission = useCallback((permissionKey) => {
    return Array.isArray(user?.permissions) && user.permissions.includes(permissionKey);
  }, [user]);

  const value = useMemo(() => ({
    authError,
    changePassword,
    enterClientPortal,
    exitClientPortal,
    hasPermission,
    isAuthenticated: Boolean(user),
    isLoading,
    login,
    logout,
    refreshCurrentUser,
    resetUserPassword,
    user
  }), [authError, changePassword, enterClientPortal, exitClientPortal, hasPermission, isLoading, login, logout, refreshCurrentUser, resetUserPassword, user]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used inside AuthProvider');
  }

  return context;
}
